import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SchoolYear, SchoolyearService } from '@awo-care/shared-core';

@Component({
  selector: 'app-schoolyear-select',
  imports: [DatePipe, FormsModule, MatFormFieldModule, MatSelectModule],
  template: `
    <mat-form-field
      appearance="outline"
      color="accent"
      data-testid="schoolyear-select"
    >
      <mat-label i18n="@@lib.schoolyear-select.schoolyear">Schuljahr</mat-label>
      <mat-select
        [(ngModel)]="schoolYear"
        [compareWith]="compareSchoolYears"
        [disabled]="disabled()"
      >
        @if (schoolYear(); as selected) {
        <mat-select-trigger>
          @if (selected.isCustomDate) {
            {{ selected.startDate.toDate() | date : 'dd.MM.yyyy' }}
          } @else {
            {{ selected.startDate.toDate().getFullYear() }}/{{
              selected.startDate.toDate().getFullYear() + 1
            }}
            @if (showDetails()) {
              <span class="date"
                >&nbsp;&middot; <ng-container i18n="@@registration.schoolYear.startDateLabel">Start</ng-container>
                {{ selected.startDate.toDate() | date : 'dd.MM.yyyy' }}</span
              >
            }
            @if (showDetails() && selected.year === nextSchoolYear()?.year) {
              <span class="date"
                >&nbsp;&middot;
                <ng-container i18n="@@registration.schoolYear.nextYearChip">Kommendes Jahr</ng-container></span
              >
            }
          }
        </mat-select-trigger>
        }
        @for (year of schoolYears(); track year.startDate) {
        <mat-option [value]="year">
          @if (year.isCustomDate) {
            {{ year.startDate.toDate() | date : 'dd.MM.yyyy' }}
          } @else {
            <span class="option-content">
              <span class="option-label">
                {{ year.startDate.toDate().getFullYear() }}/{{
                  year.startDate.toDate().getFullYear() + 1
                }}
                @if (showDetails()) {
                  <span class="date"
                    >&nbsp;&middot; <ng-container i18n="@@registration.schoolYear.startDateLabel">Start</ng-container>
                    {{ year.startDate.toDate() | date : 'dd.MM.yyyy' }}</span
                  >
                }
              </span>
              @if (showDetails() && year.year === nextSchoolYear()?.year) {
                <span
                  class="chip-badge"
                  i18n="@@registration.schoolYear.nextYearChip"
                  >Kommendes Jahr</span
                >
              }
            </span>
          }
        </mat-option>
        }
      </mat-select>
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      mat-form-field {
        width: 100%;
      }
      .date {
        font-size: 0.8rem;
        font-weight: lighter;
        color: #757575;
      }
      .option-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 0.5rem;
      }
      .option-label {
        flex: 1;
      }
      .chip-badge {
        background: #527a00;
        color: #fff;
        font-size: 10px;
        padding: 2px 8px;
        border-radius: 10px;
        font-weight: 500;
        letter-spacing: 0.3px;
        white-space: nowrap;
        flex-shrink: 0;
      }
    `,
  ],
})
export class SchoolyearSelectComponent {
  private schoolyearService = inject(SchoolyearService);

  schoolYear = model<SchoolYear | undefined>();
  showAllSchoolYears = input<boolean>(true);
  disabled = input<boolean>(false);
  autoSelectNext = input<boolean>(false);
  /** Startdatum und „Kommendes Jahr"-Badge einblenden. */
  showDetails = input<boolean>(false);
  /**
   * Jahresliste explizit vorgeben statt aus dem SchoolyearService abzuleiten —
   * die Anmeldung im Kundenportal hat eine eigene Spanne (#746).
   */
  availableYears = input<SchoolYear[] | null>(null);

  schoolYears = computed(() => {
    const explicit = this.availableYears();
    if (explicit) return explicit;
    if (this.showAllSchoolYears()) {
      return this.schoolyearService.getAllSchoolYears();
    }
    return this.schoolyearService.getUpcomingSchoolYears();
  });

  /**
   * The next school year that hasn't started yet.
   * Falls back to the first upcoming year if all are in the past (shouldn't happen).
   */
  nextSchoolYear = computed((): SchoolYear | undefined => {
    const now = new Date();
    const years = this.schoolYears();
    return years.find((y) => y.startDate.toDate() > now) ?? years[0];
  });

  constructor() {
    // Auto-select the next upcoming school year when autoSelectNext is enabled
    // and no explicit value has been set yet.
    effect(() => {
      if (this.autoSelectNext()) {
        const next = this.nextSchoolYear();
        if (next && untracked(() => this.schoolYear()) === undefined) {
          this.schoolYear.set(next);
        }
      }
    });
  }

  compareSchoolYears(
    year1: SchoolYear | null,
    year2: SchoolYear | null
  ): boolean {
    if (!year1 || !year2) return false;
    return year1.year === year2.year;
  }
}
